import { useId, useRef, useState, type DragEvent } from 'react'
import { EXPERIMENTAL_FileUpload as DsFileUpload, Field, Link, Paragraph, ValidationMessage } from '@digdir/designsystemet-react'
import { Button } from './Button'
import { Icon } from './Icon'

export type UploadFile = { uuid: string; filename: string; byte_size: number; url?: string }
export type DocumentUploadLabels = {
  upload: string
  choose: string
  dropHint: string
  uploading: string
  uploaded: string
  remove: string
  add: string
  button: string
  uploadedCount?: string
  open?: string
}

export type DocumentUploadProps = {
  documents: UploadFile[]
  onSelect: (files: File[]) => void
  onRemove: (uuid: string) => void
  labels: DocumentUploadLabels
  accept: string
  editable?: boolean
  disabled?: boolean
  canAdd?: boolean
  canRemove?: boolean
  layout?: 'compact' | 'list'
  trigger?: 'dropzone' | 'button'
  showDropzone?: boolean
  showList?: boolean
  progress?: number | null
  error?: string | null
}

// Presentation only: the caller owns validation, storage and upload progress.
export function DocumentUpload({ documents, onSelect, onRemove, labels, accept, editable = true,
  disabled = false, canAdd = true, canRemove = editable, layout = 'list', trigger = 'dropzone', showDropzone = true,
  showList = true, progress = null, error }: DocumentUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const errorId = useId()
  const hintId = `${errorId}-hint`
  const [dragging, setDragging] = useState(false)
  const busy = progress !== null
  const locked = disabled || busy
  const showPicker = editable && canAdd && (trigger === 'button' || showDropzone)

  function select(files: FileList | File[] | null) {
    if (files?.length && showPicker && !locked) onSelect(Array.from(files))
  }

  function remove(uuid: string) {
    if (canRemove && !locked) onRemove(uuid)
  }

  const dropEvents = {
    onDragOver: (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      if (showPicker && !locked) setDragging(true)
    },
    onDragLeave: () => setDragging(false),
    onDrop: (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      setDragging(false)
      select(event.dataTransfer.files)
    },
  }
  const pickerProps = {
    disabled: locked,
    onClick: () => { if (showPicker && !locked) inputRef.current?.click() },
    'aria-describedby': error ? errorId : showPicker && trigger === 'dropzone' ? hintId : undefined,
    'aria-invalid': error ? true : undefined,
  }

  return (
    <Field className={`file-upload${dragging && !locked ? ' file-upload--dragging' : ''}`}>
      {/* Keep the hidden input outside the zones: DS styles direct file inputs as overlays. */}
      <input ref={inputRef} type="file" multiple hidden accept={accept} aria-label={labels.choose}
        aria-describedby={pickerProps['aria-describedby']} aria-invalid={pickerProps['aria-invalid']}
        disabled={!showPicker || locked} onChange={event => {
          select(event.target.files)
          event.target.value = ''
        }} />
      {showPicker && trigger === 'button' && <Button size="sm" {...pickerProps}>{labels.button}</Button>}
      {showPicker && trigger === 'dropzone' && layout === 'compact' && (
        <DsFileUpload className="file-upload__compact" {...dropEvents}>
          <Icon name="upload" size={22} className="file-upload__upload-icon" aria-hidden="true" />
          <div className="file-upload__copy"><strong>{labels.upload}</strong><Field.Description id={hintId}>{labels.dropHint}</Field.Description></div>
          <Button variant="primary" {...pickerProps}>{labels.choose}</Button>
        </DsFileUpload>
      )}
      {progress !== null && (
        <progress className="file-upload__progress" max={100} value={progress} aria-label={labels.uploading}
          aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress} />
      )}
      {error && <ValidationMessage id={errorId} role="alert" className="file-upload__error">{error}</ValidationMessage>}
      {showList && documents.length > 0 && (
        <div className="file-upload__received">
        {labels.uploadedCount && <Paragraph className="file-upload__receipt" role="status"><Icon name="check" size={20} aria-hidden="true" /><strong>{labels.uploadedCount}</strong></Paragraph>}
        <ul className="file-upload__files">
          {documents.map(doc => (
            <li key={doc.uuid} className="file-upload__file">
              <span className="file-upload__file-icon" aria-hidden="true"><Icon name="check" size={18} /></span>
              <span className="file-upload__copy">
                {doc.url ? <Link href={doc.url} target="_blank" rel="noreferrer" aria-label={`${labels.open ?? labels.uploaded} ${doc.filename}`}><strong title={doc.filename}>{doc.filename}</strong></Link> : <strong title={doc.filename}>{doc.filename}</strong>}
                <span>{new Intl.NumberFormat(undefined, { style: 'unit', unit: 'megabyte', maximumFractionDigits: 1 }).format(doc.byte_size / 1024 / 1024)} · {labels.uploaded}</span>
              </span>
              {canRemove && <Button variant="ghost" size="sm" disabled={locked}
                onClick={() => remove(doc.uuid)} aria-label={`${labels.remove} ${doc.filename}`}>{labels.remove}</Button>}
            </li>
          ))}
        </ul>
        </div>
      )}
      {showPicker && trigger === 'dropzone' && layout === 'list' && (
        <DsFileUpload className="file-upload__add" {...dropEvents}>
          <Button icon="plus" {...pickerProps}>{labels.add}</Button><Field.Description id={hintId}>{labels.dropHint}</Field.Description>
        </DsFileUpload>
      )}
    </Field>
  )
}
